import { BookmarkSlashIcon } from '@heroicons/react/24/outline';
import { Button } from '../../components/Button';
import { FavoriteResponse } from '../../store/backendApi';
import { fullImage, imgUrl } from '../../utils/images';
import { useFavoriteTripUrl } from '../../hooks/favorites/useFavoriteTripUrl';

interface FavoriteTripCardProps {
  favorite: FavoriteResponse;
  onUnfollow: (tripId: string, linkSecret: string | null, e: React.MouseEvent) => void;
  onNavigate: (url: string, e: React.MouseEvent) => void;
}

/**
 * Individual favorite trip card component.
 * Displays trip information with unfollow button.
 */
export const FavoriteTripCard = ({ favorite, onUnfollow, onNavigate }: FavoriteTripCardProps) => {
  const trip = favorite.trip;
  const { tripUrl, isLoadingUrl } = useFavoriteTripUrl(trip.id);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (tripUrl) {
      onNavigate(tripUrl, e);
    }
  };

  return (
    <div
      className="flex flex-col group relative bg-surface rounded-3xl p-3
      shadow-sm ring-1 ring-gray-900/5 hover:shadow-xl
      hover:ring-gray-900/10 hover:-translate-y-1 transition-all duration-300"
    >
      <a
        href={tripUrl || '#'}
        onClick={handleClick}
        className={`flex flex-col flex-1 ${
          !tripUrl && !isLoadingUrl ? 'cursor-not-allowed opacity-60' : ''
        }`}
      >
        <div className="relative w-full overflow-hidden rounded-2xl">
          <img
            alt=""
            src={
              trip.headerFileName
                ? imgUrl(trip.id, fullImage(trip.headerFileName))
                : '/default-trip-header.jpg'
            }
            className="aspect-[3/2] w-full bg-muted object-cover
            group-hover:scale-105 transition-transform duration-500"
            onError={(e) => (e.currentTarget.src = '/default-trip-header.jpg')}
          />
          <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-2xl" />
          {trip.postCount !== undefined && (
            <div
              className="absolute top-3 right-3 z-10 rounded-full
              bg-white/90 px-3 py-1 text-xs font-bold text-main
              shadow-sm backdrop-blur-md"
            >
              {trip.postCount} {trip.postCount === 1 ? 'post' : 'posts'}
            </div>
          )}
        </div>
        <div className="mt-4 px-2 pb-2 flex-1">
          <div className="flex items-center gap-x-4">
            <img
              src={`https://media.${process.env.REACT_APP_HOST}/users/${
                trip.ownerId
              }-thumbnail.jpg?v=${Date.now()}`}
              alt=""
              className="h-10 w-10 rounded-full bg-subtle ring-2
              ring-white shadow-sm object-cover"
              onError={(e) => (e.currentTarget.src = '/default-profile-picture.jpg')}
            />
            <div className="flex flex-col flex-1">
              <h3
                className="text-lg font-bold text-main
                group-hover:text-brand transition-colors"
              >
                {trip.title}
              </h3>
              {trip.startDate && trip.endDate && (
                <div
                  className="flex items-center gap-x-1 text-xs
                  font-medium text-muted"
                >
                  <time dateTime={trip.startDate}>{trip.startDate}</time>
                  <span>•</span>
                  <time dateTime={trip.endDate}>{trip.endDate}</time>
                </div>
              )}
            </div>
          </div>
        </div>
      </a>

      {/* Unfollow Button */}
      <div className="px-2 pb-2 mt-2">
        <Button
          variant="secondary"
          size="md"
          onClick={(e) => onUnfollow(trip.id, favorite.lastKnownLinkSecret ?? null, e)}
          iconBefore={<BookmarkSlashIcon className="w-4 h-4" />}
          fullWidth
          aria-label="Unfollow this trip"
        >
          Unfollow
        </Button>
      </div>
    </div>
  );
};
